import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import PageLoader from '../components/PageLoader';
import APIClient from '../api/axios';
import {
  FiPlus, FiX, FiCalendar, FiMoreVertical,
  FiFilter,
} from 'react-icons/fi';
import './TaskPlanner.css';

const PRIORITY_CFG = {
  high:   { label: 'High',   color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  medium: { label: 'Medium', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  low:    { label: 'Low',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
};
const STATUS_CFG = {
  pending:    { label: 'Pending',     color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  'in-progress': { label: 'In Progress', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  completed:  { label: 'Completed',   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  cancelled:  { label: 'Cancelled',   color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
};
const TYPE_CFG = {
  staff:      { label: 'Staff',      color: '#7c3aed', bg: '#f5f3ff' },
  supervisor: { label: 'Supervisor', color: '#0891b2', bg: '#ecfeff' },
};

// task type icons keyed by title pattern
const TASK_ICONS = {
  'site visit':        '📍',
  'follow-up':        '👤',
  'vendor':           '🚚',
  'quotation':        '📄',
  'payment':          '₹',
  'mail':             '✉️',
  'purchase':         '🛒',
  'quality':          '🛡️',
  'material':         '📦',
  'contractor':       '👷',
  'progress':         '📈',
  'planning':         '📅',
  'default':          '📋',
};
function taskIcon(title = '') {
  const t = title.toLowerCase();
  for (const [k, v] of Object.entries(TASK_ICONS)) {
    if (k !== 'default' && t.includes(k)) return v;
  }
  return TASK_ICONS.default;
}

function Badge({ value, config }) {
  const cfg = config[value] || Object.values(config)[0];
  return <span className="tp-badge" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border || cfg.bg}` }}>{cfg.label}</span>;
}

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function today() { return new Date().toISOString().split('T')[0]; }
function tomorrow() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

export default function TaskPlanner() {
  const [selectedDate, setSelectedDate] = useState(today());
  const [tasks, setTasks] = useState([]);
  const [tomorrowTasks, setTomorrowTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({ taskType: '', priority: '', status: '', assignedTo: '', project: '' });
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [taskType, setTaskType] = useState('staff'); // for modal default
  const [showPlanTypeMenu, setShowPlanTypeMenu] = useState(false);
  const [dateBeforePlanTomorrow, setDateBeforePlanTomorrow] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const dateInputRef = useRef(null);

  useEffect(() => { loadTasks(); loadTomorrow(); }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Roll over incomplete past tasks to tomorrow on page load
    APIClient.post('/tasks/rollover').then(r => {
      if (r.data.rolled > 0) {
        loadTasks();
        loadTomorrow();
      }
    }).catch(() => {});

    Promise.all([
      APIClient.get('/projects').then(r => setProjects(r.data)).catch(() => {}),
      APIClient.get('/users').then(r => setUsers(r.data)).catch(() => {}),
      APIClient.get('/roles').then(r => setRoles(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadTasks() {
    const params = { date: selectedDate };
    if (filters.priority)   params.priority   = filters.priority;
    if (filters.status)     params.status     = filters.status;
    if (filters.assignedTo) params.assignedTo = filters.assignedTo;
    if (filters.project)    params.project    = filters.project;
    if (filters.taskType)   params.taskType   = filters.taskType;
    APIClient.get('/tasks', { params })
      .then(r => setTasks(r.data)).catch(() => {});
  }
  function loadTomorrow() {
    APIClient.get('/tasks/tomorrow').then(r => setTomorrowTasks(r.data)).catch(() => {});
  }

  function applyFilters() { loadTasks(); }
  function clearFilters() { setFilters({ taskType: '', priority: '', status: '', assignedTo: '', project: '' }); }

  async function updateStatus(id, status) {
    try {
      const r = await APIClient.put(`/tasks/${id}`, { status });
      setTasks(prev => prev.map(t => t._id === id ? r.data : t));
      toast.success('Status updated');
    } catch { toast.error('Failed'); }
    setOpenMenuId(null);
  }
  async function deleteTask(id) {
    setOpenMenuId(null);
    setConfirm({ id });
  }
  async function doDeleteTask() {
    const { id } = confirm; setConfirm(null);
    try {
      await APIClient.delete(`/tasks/${id}`);
      setTasks(prev => prev.filter(t => t._id !== id));
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete'); }
  }

  // split tasks
  const staffTasks = tasks.filter(t => !t.taskType || t.taskType === 'staff');
  const supervisorTasks = tasks.filter(t => t.taskType === 'supervisor');

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    pending: tasks.filter(t => t.status === 'pending').length,
  };

  // Donut data
  const total = stats.total || 1;
  const donutSlices = [
    { pct: stats.completed / total, color: '#2563eb' },
    { pct: stats.inProgress / total, color: '#f59e0b' },
    { pct: stats.pending / total, color: '#ef4444' },
  ];
  function buildDonut() {
    const R = 38, cx = 50, cy = 50;
    let paths = [], start = -Math.PI / 2;
    for (const s of donutSlices) {
      const angle = s.pct * 2 * Math.PI;
      if (angle < 0.01) continue;
      const end = start + angle;
      const x1 = cx + R * Math.cos(start), y1 = cy + R * Math.sin(start);
      const x2 = cx + R * Math.cos(end),   y2 = cy + R * Math.sin(end);
      paths.push(<path key={s.color} d={`M${cx},${cy} L${x1},${y1} A${R},${R} 0 ${angle > Math.PI ? 1 : 0},1 ${x2},${y2} Z`} fill={s.color} />);
      start = end;
    }
    return paths;
  }

  const isToday = selectedDate === today();
  const dateLbl = isToday ? `${fmt(selectedDate + 'T00:00:00')} (Today)` : fmt(selectedDate + 'T00:00:00');

  return (
    <Layout>
      {loading && <PageLoader text="Loading tasks..." />}
      <div className="tp-root">

        {/* ── Top controls ── */}
        <div className="tp-topbar">
          <div className="tp-date-box">
            <span className="tp-date-lbl">Select Date</span>
            <div
              className="tp-date-input-wrap"
              onClick={() => {
                const el = dateInputRef.current;
                if (!el) return;
                if (typeof el.showPicker === 'function') el.showPicker();
                else el.focus();
              }}
            >
              <FiCalendar size={14} />
              <span className="tp-date-val">{dateLbl}</span>
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="tp-date-hidden"
              />
            </div>
          </div>
          <div className="tp-plan-tomorrow-wrap">
            <button className="tp-btn-primary" onClick={() => setShowPlanTypeMenu(v => !v)}>
              <FiCalendar size={13} /> Plan for Tomorrow
            </button>
            {showPlanTypeMenu && (
              <div className="tp-dropdown">
                <button onClick={() => { setDateBeforePlanTomorrow(selectedDate); setSelectedDate(tomorrow()); setTaskType('staff'); setEditTask(null); setShowModal(true); setShowPlanTypeMenu(false); }}>
                  Staff Task
                </button>
                <button onClick={() => { setDateBeforePlanTomorrow(selectedDate); setSelectedDate(tomorrow()); setTaskType('supervisor'); setEditTask(null); setShowModal(true); setShowPlanTypeMenu(false); }}>
                  Supervisor Task
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Overview + Donut + Plan Ahead ── */}
        <div className="tp-overview-row">
          {/* Today's Overview */}
          <div className="tp-card tp-overview-card">
            <h3 className="tp-section-title">Today's Overview</h3>
            <div className="tp-overview-stats">
              {[
                { icon: '📋', label: 'Total Tasks',  val: stats.total,      color: '#7c3aed', iconBg: '#f5f3ff' },
                { icon: '✅', label: 'Completed',    val: stats.completed,  color: '#16a34a', iconBg: '#f0fdf4' },
                { icon: '⏰', label: 'In Progress',  val: stats.inProgress, color: '#f59e0b', iconBg: '#fffbeb' },
                { icon: '⚠️', label: 'Pending',      val: stats.pending,    color: '#ef4444', iconBg: '#fef2f2' },
              ].map((s, i) => (
                <div key={i} className="tp-ov-stat">
                  <div className="tp-ov-icon-wrap" style={{ background: s.iconBg }}>{s.icon}</div>
                  <p className="tp-ov-label">{s.label}</p>
                  <p className="tp-ov-num" style={{ color: s.color }}>{s.val}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Task Status Donut */}
          <div className="tp-card tp-donut-card">
            <h3 className="tp-section-title">Task Status</h3>
            <div className="tp-donut-wrap">
              <svg viewBox="0 0 100 100" width="110" height="110">
                {stats.total === 0
                  ? <circle cx="50" cy="50" r="38" fill="#f1f5f9" />
                  : buildDonut()}
                <circle cx="50" cy="50" r="26" fill="#fff" />
                <text x="50" y="46" textAnchor="middle" fontSize="12" fontWeight="800" fill="#0f172a">{stats.total}</text>
                <text x="50" y="57" textAnchor="middle" fontSize="6.5" fill="#94a3b8">Total Tasks</text>
              </svg>
              <div className="tp-donut-legend">
                {[
                  { color: '#2563eb', label: 'Completed',  count: stats.completed,  pct: Math.round(stats.completed  / total * 100) },
                  { color: '#f59e0b', label: 'In Progress',count: stats.inProgress, pct: Math.round(stats.inProgress / total * 100) },
                  { color: '#ef4444', label: 'Pending',    count: stats.pending,    pct: Math.round(stats.pending    / total * 100) },
                ].map((l, i) => (
                  <div key={i} className="tp-legend-row">
                    <span className="tp-legend-dot" style={{ background: l.color }} />
                    <span className="tp-legend-label">{l.label}</span>
                    <span className="tp-legend-count">{l.count} ({l.pct}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* ── Staff Tasks + Supervisor Tasks ── */}
        <div className="tp-tasks-grid">
          <TaskTable
            title="Staff Tasks" subtitle="Manage your daily operational tasks"
            iconColor="#7c3aed" iconBg="#f5f3ff" icon="👤"
            tasks={staffTasks}
            onAdd={() => { setTaskType('staff'); setEditTask(null); setShowModal(true); }}
            onEdit={(t) => { setEditTask(t); setTaskType(t.taskType || 'staff'); setShowModal(true); }}
            onDelete={deleteTask}
            onStatusChange={updateStatus}
            openMenuId={openMenuId} setOpenMenuId={setOpenMenuId}
          />
          <TaskTable
            title="Supervisor Tasks" subtitle="Manage site and execution related tasks"
            iconColor="#0891b2" iconBg="#ecfeff" icon="👷"
            tasks={supervisorTasks}
            onAdd={() => { setTaskType('supervisor'); setEditTask(null); setShowModal(true); }}
            onEdit={(t) => { setEditTask(t); setTaskType(t.taskType || 'supervisor'); setShowModal(true); }}
            onDelete={deleteTask}
            onStatusChange={updateStatus}
            openMenuId={openMenuId} setOpenMenuId={setOpenMenuId}
          />
        </div>

        {/* ── Tomorrow's Plan + Benefits + Task Filters ── */}
        <div className="tp-bottom-grid">
          {/* Tomorrow's Plan */}
          <div className="tp-card">
            <div className="tp-card-header">
              <div>
                <h3 className="tp-section-title tp-no-mb">Tomorrow's Plan ({fmt(tomorrow() + 'T00:00:00')})</h3>
                <p className="tp-section-sub">Tasks planned for tomorrow</p>
              </div>
              <button className="tp-link-btn" onClick={() => setSelectedDate(tomorrow())}>View All</button>
            </div>
            <div className="tp-tomorrow-list">
              {tomorrowTasks.length === 0 && <p className="tp-empty">No tasks planned for tomorrow yet.</p>}
              {tomorrowTasks.slice(0, 5).map(t => (
                <div key={t._id} className="tp-tomorrow-item">
                  <span className="tp-tomorrow-time">{fmtTime(t.plannedAt) || '—'}</span>
                  <span className="tp-tomorrow-icon">{taskIcon(t.title)}</span>
                  <div className="tp-tomorrow-info">
                    <p className="tp-tomorrow-title">{t.title}</p>
                    <p className="tp-tomorrow-sub">{t.project ? `${t.project.projectId ? t.project.projectId + ' – ' : ''}${t.project.title}` : t.description || ''}</p>
                  </div>
                  <span className="tp-type-chip" style={{ color: TYPE_CFG[t.taskType || 'staff'].color, background: TYPE_CFG[t.taskType || 'staff'].bg }}>
                    {TYPE_CFG[t.taskType || 'staff'].label}
                  </span>
                </div>
              ))}
            </div>
          </div>


          {/* Task Filters */}
          <div className="tp-card tp-filters-card">
            <h3 className="tp-section-title"><FiFilter size={14} /> Task Filters</h3>
            <div className="tp-filters-grid">
              <div className="tp-filter-group">
                <label>Task Type</label>
                <select value={filters.taskType} onChange={e => setFilters(f => ({ ...f, taskType: e.target.value }))}>
                  <option value="">All</option>
                  <option value="staff">Staff</option>
                  <option value="supervisor">Supervisor</option>
                </select>
              </div>
              <div className="tp-filter-group">
                <label>Priority</label>
                <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
                  <option value="">All</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div className="tp-filter-group">
                <label>Status</label>
                <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="tp-filter-group">
                <label>Assigned To</label>
                <select value={filters.assignedTo} onChange={e => setFilters(f => ({ ...f, assignedTo: e.target.value }))}>
                  <option value="">All</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                </select>
              </div>
              <div className="tp-filter-group">
                <label>Project</label>
                <ProjectSearchSelect
                  projects={projects}
                  value={filters.project}
                  onChange={p => setFilters(f => ({ ...f, project: p ? p._id : '' }))}
                  allOption={true}
                  placeholder="All"
                />
              </div>
            </div>
            <div className="tp-filter-actions">
              <button className="tp-btn-ghost" onClick={clearFilters}><FiX size={12} /> Clear Filters</button>
              <button className="tp-btn-primary" onClick={applyFilters}>Apply Filters</button>
            </div>
          </div>
        </div>

        {/* ── Tip footer ── */}
        <div className="tp-tip">
          <span>💡</span>
          Tip: Plan your tasks in advance and stay ahead. Well planned work leads to successful project delivery.
        </div>

      </div>

      {confirm && (
        <div className="tp-overlay" onClick={() => setConfirm(null)}>
          <div className="tp-modal" style={{ maxWidth: 380, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', margin: '.5rem 0' }}>🗑️</div>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Delete Task</h3>
            <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Delete this task? This cannot be undone.</p>
            <div className="tp-modal-footer">
              <button className="tp-btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="tp-btn-primary" style={{ flex: 1, background: '#dc2626' }} onClick={doDeleteTask}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <TaskModal
          task={editTask}
          defaultType={taskType}
          defaultDate={selectedDate}
          projects={projects}
          users={users}
          roles={roles}
          onClose={() => {
            setShowModal(false); setEditTask(null);
            if (dateBeforePlanTomorrow) { setSelectedDate(dateBeforePlanTomorrow); setDateBeforePlanTomorrow(null); }
          }}
          onSaved={(t) => {
            setTasks(prev => editTask ? prev.map(x => x._id === t._id ? t : x) : [t, ...prev]);
            if (t.plannedAt && new Date(t.plannedAt).toISOString().split('T')[0] === tomorrow()) loadTomorrow();
            setShowModal(false); setEditTask(null);
            if (dateBeforePlanTomorrow) { setSelectedDate(dateBeforePlanTomorrow); setDateBeforePlanTomorrow(null); }
          }}
        />
      )}
    </Layout>
  );
}

/* ── Task Table Component ── */
function TaskTable({ title, subtitle, icon, iconColor, iconBg, tasks, onAdd, onEdit, onDelete, onStatusChange, openMenuId, setOpenMenuId }) {
  return (
    <div className="tp-card tp-task-card">
      <div className="tp-task-header">
        <div className="tp-task-header-left">
          <div className="tp-task-icon" style={{ background: iconBg, color: iconColor }}>{icon}</div>
          <div>
            <h3 className="tp-task-title">{title}</h3>
            <p className="tp-task-sub">{subtitle}</p>
          </div>
        </div>
        <button className="tp-btn-primary tp-btn-sm" onClick={onAdd}><FiPlus size={13} /> Add Task</button>
      </div>
      <div className="tp-table-wrap">
      <table className="tp-table">
        <thead>
          <tr><th>Task</th><th>Related To</th><th>Priority</th><th>Due Date</th><th>Status</th><th>Action</th></tr>
        </thead>
        <tbody>
          {tasks.length === 0 && <tr><td colSpan={6} className="tp-empty">No tasks for this date</td></tr>}
          {tasks.map(t => (
            <tr key={t._id} className={t.status === 'completed' ? 'tp-row-done' : ''}>
              <td>
                <div className="tp-task-name-cell">
                  <span className="tp-task-ico">{taskIcon(t.title)}</span>
                  <span className="tp-task-name">{t.title}</span>
                </div>
              </td>
              <td className="tp-related">
                {t.project ? `${t.project.projectId ? t.project.projectId + ' – ' : ''}${t.project.title}` : t.relatedTo || 'General'}
              </td>
              <td><Badge value={t.priority} config={PRIORITY_CFG} /></td>
              <td className="tp-date-cell">{t.plannedAt ? fmt(t.plannedAt) : '—'}</td>
              <td><Badge value={t.status} config={STATUS_CFG} /></td>
              <td>
                <div className="tp-action-wrap" style={{ position: 'relative' }}>
                  <button className="tp-menu-btn" onClick={() => setOpenMenuId(openMenuId === t._id ? null : t._id)}>
                    <FiMoreVertical size={15} />
                  </button>
                  {openMenuId === t._id && (
                    <div className="tp-dropdown">
                      <button onClick={() => onEdit(t)}>✏️ Edit</button>
                      {Object.entries(STATUS_CFG).filter(([k]) => k !== t.status).map(([k, v]) => (
                        <button key={k} onClick={() => onStatusChange(t._id, k)}>
                          → Mark {v.label}
                        </button>
                      ))}
                      <button className="tp-dd-del" onClick={() => onDelete(t._id)}>🗑️ Delete</button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {tasks.length === 0 ? null : (
        <div className="tp-view-all-row">
          <span className="tp-view-all-count">{tasks.length} task{tasks.length !== 1 ? 's' : ''} total</span>
        </div>
      )}
    </div>
  );
}

/* ── Task Modal ── */
function TaskModal({ task, defaultType, defaultDate, projects, users, roles, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    taskType: task?.taskType || defaultType || 'staff',
    project: task?.project?._id || task?.project || '',
    assignedToRole: task?.assignedTo?.role?._id || task?.assignedTo?.role || '',
    assignedTo: task?.assignedTo?._id || task?.assignedTo || '',
    relatedTo: task?.relatedTo || '',
    category: task?.category || 'other',
    priority: task?.priority || 'medium',
    status: task?.status || 'pending',
    plannedDate: task?.plannedAt ? new Date(task.plannedAt).toISOString().split('T')[0] : defaultDate,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const wantedStaffType = form.taskType === 'supervisor' ? 'site' : 'office';
  const taskTypeRoles = roles.filter(r => r.staffType === wantedStaffType);
  const staffTypeUsers = users.filter(u => (u.role?.staffType || 'office') === wantedStaffType);
  const assignableUsers = form.assignedToRole
    ? staffTypeUsers.filter(u => (u.role?._id || u.role) === form.assignedToRole)
    : staffTypeUsers;

  async function save() {
    if (!form.title.trim()) return toast.error('Task title is required');
    if (!task && form.plannedDate && form.plannedDate < today()) {
      return toast.error('Planned date cannot be in the past');
    }
    setSaving(true);
    try {
      let plannedAt = null;
      if (form.plannedDate) {
        plannedAt = new Date(`${form.plannedDate}T09:00:00`);
      }
      const payload = { ...form, plannedAt };
      delete payload.plannedDate;
      delete payload.assignedToRole; // UI-only helper, not part of the Task schema
      if (!payload.project) delete payload.project;
      if (!payload.assignedTo) delete payload.assignedTo;
      const r = task
        ? await APIClient.put(`/tasks/${task._id}`, payload)
        : await APIClient.post('/tasks', payload);
      toast.success(task ? 'Task updated' : 'Task created');
      onSaved(r.data);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="tp-overlay">
      <div className="tp-modal" onClick={e => e.stopPropagation()}>
        <div className="tp-modal-header">
          <h3>{task ? 'Edit Task' : 'Add Task'}</h3>
          <button className="tp-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="tp-modal-body">
          <div className="tp-mrow">
            <div className="tp-mgroup tp-mfull">
              <label>Task Title *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Customer Follow-up" />
            </div>
          </div>
          <div className="tp-mrow">
            <div className="tp-mgroup">
              <label>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="tp-mgroup">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="tp-mgroup">
              <label>Date</label>
              <input type="date" value={form.plannedDate} min={task ? undefined : today()} onChange={e => set('plannedDate', e.target.value)} />
            </div>
          </div>
          <div className="tp-mrow">
            <div className="tp-mgroup">
              <label>Role</label>
              <select value={form.assignedToRole} onChange={e => { set('assignedToRole', e.target.value); set('assignedTo', ''); }}>
                <option value="">All Roles</option>
                {taskTypeRoles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>
            <div className="tp-mgroup">
              <label>Assigned To</label>
              <select value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                <option value="">— Unassigned —</option>
                {assignableUsers.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          {form.taskType !== 'staff' && (
            <div className="tp-mrow">
              <div className="tp-mgroup tp-mfull">
                <label>Project</label>
                <select value={form.project} onChange={e => set('project', e.target.value)}>
                  <option value="">— None —</option>
                  {projects.map(p => <option key={p._id} value={p._id}>{p.projectId ? `${p.projectId} – ` : ''}{p.title}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="tp-mrow">
            <div className="tp-mgroup tp-mfull">
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional details…" />
            </div>
          </div>
        </div>
        <div className="tp-modal-footer">
          <button className="tp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="tp-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : task ? 'Update Task' : 'Add Task'}</button>
        </div>
      </div>
    </div>
  );
}
